import axios, { AxiosError } from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import type { GenericApiResponse, ApiError } from '@/types/ApiResponse'
import { PUBLIC_ENDPOINTS } from '@/config/apiConstants'
import { tokenService } from './TokenService'
import i18n from '@/i18n'

// Module-level singleton state shared across ALL BaseApiClient instances.
// This prevents the race condition where multiple service instances
// simultaneously attempt to refresh the token.
let isRefreshing = false
let failedQueue: Array<{
    resolve: (value?: unknown) => void
    reject: (reason?: any) => void
}> = []
let isLoggingOut = false

/**
 * Production-Grade Base API Client
 */
export class BaseApiClient {
    private client: AxiosInstance

    constructor() {
        this.client = axios.create({
            baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json'
            },
            withCredentials: true // Critical for HttpOnly cookies
        })

        this.setupInterceptors()
    }

    private setupInterceptors(): void {
        this.client.interceptors.request.use(
            (config) => {
                const locale = this.getCurrentLocale()
                if (locale) {
                    config.headers['Accept-Language'] = locale
                }
                config.headers['X-Requested-With'] = 'XMLHttpRequest'
                return config
            },
            (error) => Promise.reject(error)
        )

        this.client.interceptors.response.use(
            (response) => response,
            async (error: AxiosError) => {
                const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

                if (error.response?.status === 401 && !originalRequest._retry) {
                    if (PUBLIC_ENDPOINTS.includes(originalRequest.url as any)) {
                        return Promise.reject(this.normalizeError(error))
                    }

                    if (isRefreshing) {
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject })
                        })
                            .then(() => this.client(originalRequest))
                            .catch((err) => Promise.reject(err))
                    }

                    originalRequest._retry = true
                    isRefreshing = true

                    try {
                        await this.refreshToken()
                        this.processQueue(null)
                        return this.client(originalRequest)
                    } catch (firstError) {
                        // Retry once — transient failures (timeout, network) shouldn't kill a 30-day session
                        try {
                            await this.refreshToken()
                            this.processQueue(null)
                            return this.client(originalRequest)
                        } catch (retryError) {
                            // normalizeError'dan geçmesi şart: tüketiciler code/message/errors
                            // alanlarını okuyor, ham axios hatası verilirse hepsi undefined gelir.
                            const normalized = this.normalizeError(retryError as AxiosError)
                            this.processQueue(normalized)
                            this.handleAuthFailure()
                            return Promise.reject(normalized)
                        }
                    } finally {
                        isRefreshing = false
                    }
                }

                return Promise.reject(this.normalizeError(error))
            }
        )
    }

    private getCurrentLocale(): string {
        try {
            // Fix: Read from localStorage which is the source of truth for persistence
            // This avoids hardcoding and circular dependency issues with i18n instance
            return localStorage.getItem('user-locale') || 'tr'
        } catch {
            return 'en'
        }
    }

    private async refreshToken(): Promise<void> {
        try {
            await this.client.post('/auth/refresh-token', {})
        } catch (error) {
            throw error
        }
    }

    /**
     * Axios interceptor'ı dışındaki akışlar için oturum tazeleme.
     * Örn. WebSocket 1008 / handshake reddi sonrası chat store,
     * yeniden bağlanmadan önce access token cookie'sini bununla yeniler.
     */
    async refreshSession(): Promise<void> {
        await this.refreshToken()
    }

    private processQueue(error: any): void {
        failedQueue.forEach((promise) => {
            if (error) {
                promise.reject(error)
            } else {
                promise.resolve()
            }
        })
        failedQueue = []
    }

    private handleAuthFailure(): void {
        // Re-entrancy guard: if another instance already triggered logout, skip
        if (isLoggingOut) {
            return
        }
        isLoggingOut = true

        tokenService.clearAuthState()

        if (typeof window !== 'undefined') {
            // Prevent redirect loop if already on login page
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login?expired=true'
            }
        }
        // isLoggingOut stays true — page will redirect, no need to reset
    }

    private normalizeError(error: AxiosError): ApiError {
        if (error.response?.data) {
            const data = error.response.data as any
            return {
                code: data.code || 'UNKNOWN_ERROR',
                message: data.message || 'An unexpected error occurred',
                errors: data.errors,
                timestamp: data.timestamp || new Date().toISOString()
            }
        }
        return {
            code: 'NETWORK_ERROR',
            message: error.message || 'Network error occurred',
            timestamp: new Date().toISOString()
        }
    }

    private unwrapResponse<T>(response: AxiosResponse<GenericApiResponse<T>>): T {
        const apiResponse = response.data

        // Gövde beklenen envelope değilse (ör. reverse proxy backend'i atlayıp SPA'nın
        // index.html'ini 200 ile döndürdüğünde) `success` undefined olur ve aşağıdaki
        // dal tüm alanları undefined olan bir hata fırlatırdı — çağıran taraf
        // `e.message` bulamayıp anlamsız mesaj gösterirdi. Önce şekli doğrula.
        if (!apiResponse || typeof apiResponse !== 'object' || !('success' in apiResponse)) {
            throw {
                code: 'INVALID_RESPONSE',
                message: i18n.global.t('common.unexpectedResponse', { status: response.status }),
                timestamp: new Date().toISOString()
            } as ApiError
        }

        if (!apiResponse.success) {
            throw {
                code: apiResponse.code || 'UNKNOWN_ERROR',
                message: apiResponse.message || 'An unexpected error occurred',
                errors: apiResponse.errors,
                timestamp: apiResponse.timestamp || new Date().toISOString()
            } as ApiError
        }
        return this.normalizePage(apiResponse.data) as T
    }

    /**
     * Spring Boot 4 (Spring Data 4) artık Page'i PagedModel formatında serialize ediyor:
     * { content, page: { size, number, totalElements, totalPages } }.
     *
     * Frontend'in 22 view'ı eski format'a (`{ content, totalElements, ... }`) bağlı olduğu için
     * burada page.* alanlarını root'a flatten ederiz — geriye dönük uyumluluk.
     */
    private normalizePage(data: any): any {
        if (data && typeof data === 'object'
                && Array.isArray((data as any).content)
                && (data as any).page
                && typeof (data as any).page === 'object') {
            const p = (data as any).page
            return { ...data, ...p }
        }
        return data
    }

    async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.client.get<GenericApiResponse<T>>(url, config)
        return this.unwrapResponse(response)
    }

    async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.client.post<GenericApiResponse<T>>(url, data, config)
        return this.unwrapResponse(response)
    }

    async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.client.put<GenericApiResponse<T>>(url, data, config)
        return this.unwrapResponse(response)
    }

    async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.client.delete<GenericApiResponse<T>>(url, config)
        return this.unwrapResponse(response)
    }

    async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.client.patch<GenericApiResponse<T>>(url, data, config)
        return this.unwrapResponse(response)
    }

    /**
     * POST that bypasses the GenericApiResponse envelope. Use for endpoints
     * that stream binary content (e.g. CSV/Excel downloads). The caller is
     * responsible for picking a sensible {@code responseType} in {@code config}.
     */
    async postRaw<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.client.post<T>(url, data, config)
        return response.data
    }
}

export const baseApiClient = new BaseApiClient()

/**
 * Test-only helpers to inspect/reset module-level singleton state.
 * Do NOT use in production code.
 */
export const __test__ = {
    getIsRefreshing: () => isRefreshing,
    setIsRefreshing: (v: boolean) => { isRefreshing = v },
    getFailedQueue: () => failedQueue,
    getIsLoggingOut: () => isLoggingOut,
    setIsLoggingOut: (v: boolean) => { isLoggingOut = v },
    reset: () => { isRefreshing = false; failedQueue = []; isLoggingOut = false }
}
